import React, { useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Form,
  Header,
  Icon,
  Button,
  Segment,
  Message,
  Dropdown,
} from 'semantic-ui-react';
import { SG_CREATE_SLACK_TASK, SG_GET_SLACK_WORKSPACE_USERS } from 'constants/actions';
import Navigation from './Navigation';
import { useWorkspaceId } from './useWorkspaceId';

function CreateTask() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const workspaceId = useWorkspaceId();
  
  const { workspaceUsers, taskCreated, taskCreationPending, message } = useSelector(
    (s) => s.slack_tasks || {},
    shallowEqual
  );

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedTo: '',
    priority: '3',
    dueDate: '',
    estimate: '',
    notifyOnSlack: true,
  });

  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (!workspaceId) return;
    
    // Fetch workspace users for assignment dropdown
    dispatch({
      type: SG_GET_SLACK_WORKSPACE_USERS,
      payload: { workspaceId, queryParams: 'active=true' },
    });
  }, [dispatch, workspaceId]);

  React.useEffect(() => {
    if (taskCreated) {
      // Navigate to task list after successful creation
      setTimeout(() => {
        navigate('/app/slack-tasks/tasks');
      }, 2000);
    }
  }, [taskCreated, navigate]);

  const handleChange = (e, { name, value }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleCheckboxChange = (e, { name, checked }) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const taskData = {
      title: formData.title,
      description: formData.description,
      assignedTo: formData.assignedTo || undefined,
      priority: parseInt(formData.priority),
      dueDate: formData.dueDate || undefined,
      estimate: formData.estimate || undefined,
      notifyOnSlack: formData.notifyOnSlack,
    };

    dispatch({
      type: SG_CREATE_SLACK_TASK,
      payload: { workspaceId, taskData },
    });
  };

  const userOptions = Array.isArray(workspaceUsers)
    ? workspaceUsers.map((user) => ({
        key: user.id,
        text: user.name,
        value: user.id,
        description: user.email,
      }))
    : [];

  const priorityOptions = [
    { key: '1', text: '🔴 Urgent', value: '1' },
    { key: '2', text: '🟠 High', value: '2' },
    { key: '3', text: '🟡 Medium', value: '3' },
    { key: '4', text: '⚪ Low', value: '4' },
  ];

  return (
    <Page>
      <Navigation />

      <Header as="h1">
        <Icon name="plus circle" />
        Create New Task
        <Header.Subheader>
          Add a task and optionally notify on Slack
        </Header.Subheader>
      </Header>

      {taskCreated && (
        <Message success>
          <Message.Header>Task created successfully!</Message.Header>
          <p>{message || 'Redirecting to task list...'}</p>
        </Message>
      )}

      <Segment>
        <Form loading={taskCreationPending}>
          <Form.Input
            label="Task Title"
            name="title"
            placeholder="e.g., Fix login authentication issue"
            value={formData.title}
            onChange={handleChange}
            error={errors.title}
            required
          />

          <Form.TextArea
            label="Description"
            name="description"
            placeholder="Detailed description of the task..."
            value={formData.description}
            onChange={handleChange}
            rows={4}
          />

          <Form.Group widths="equal">
            <Form.Field>
              <label>Assign To</label>
              <Dropdown
                placeholder="Select a user (optional)"
                fluid
                search
                selection
                clearable
                options={userOptions}
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleChange}
              />
            </Form.Field>

            <Form.Field>
              <label>Priority</label>
              <Dropdown
                placeholder="Select priority"
                fluid
                selection
                options={priorityOptions}
                name="priority"
                value={formData.priority}
                onChange={handleChange}
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Input
              label="Due Date"
              name="dueDate"
              type="date"
              value={formData.dueDate}
              onChange={handleChange}
            />

            <Form.Input
              label="Estimate"
              name="estimate"
              placeholder="e.g., 2 hours, 3 days"
              value={formData.estimate}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Checkbox
            label="Send Slack notification to assignee"
            name="notifyOnSlack"
            checked={formData.notifyOnSlack}
            onChange={handleCheckboxChange}
          />

          <ButtonGroup>
            <Button
              primary
              size="large"
              onClick={handleSubmit}
              disabled={taskCreationPending}
            >
              <Icon name="plus" />
              Create Task
            </Button>
            <Button
              basic
              size="large"
              onClick={() => navigate('/app/slack-tasks/tasks')}
            >
              Cancel
            </Button>
          </ButtonGroup>
        </Form>
      </Segment>

      <InfoBox>
        <Icon name="info circle" color="blue" />
        <div>
          <strong>Tip:</strong> When you assign a task and enable Slack notification,
          the assignee will receive an interactive message in Slack with options to
          start working or view details.
        </div>
      </InfoBox>
    </Page>
  );
}

const Page = styled.div`
  padding: 20px;
  max-width: 800px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const InfoBox = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #e8f4fd;
  border-radius: 4px;
  margin-top: 20px;

  i.icon {
    flex-shrink: 0;
  }
`;

export default CreateTask;
